// CRM Email Batches — rehoused from classic EmailBatches.js.
// Two-column shell: left batch rail (search + status filter), right detail panel.
// Every classic endpoint and action preserved. Presentation rebuilt to the CRM system.
import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import API_BASE from '../../config';

const DESTINATIONS = [
  { value: 'pipedrive_csv',  label: 'Pipedrive',    short: 'Pipedrive', c: '#1A7A4C', bg: '#E4F3EB' },
  { value: 'hubspot_csv',    label: 'HubSpot',      short: 'HubSpot',   c: '#FF7A59', bg: '#FFECE6' },
  { value: 'bigin_csv',      label: 'Zoho Bigin',   short: 'Bigin',     c: '#E42527', bg: '#FCE6E6' },
  { value: 'zoho_crm_csv',   label: 'Zoho CRM',     short: 'Zoho CRM',  c: '#C8202E', bg: '#FBE5E7' },
  { value: 'salesforce_csv', label: 'Salesforce',   short: 'Salesforce',c: '#00A1E0', bg: '#E0F4FC' },
];
function dest(value) { return DESTINATIONS.find(d => d.value === value) || { label: value, short: value, c: '#64748B', bg: '#EEF1F4' }; }

// Small brand glyph per destination. Simple line marks in the CRM icon style.
function DestMark({ value, size = 15 }) {
  const d = dest(value);
  const p = { fill: 'none', stroke: d.c, strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
  const marks = {
    pipedrive_csv:  <><circle cx="8" cy="6" r="3"/><path d="M8 9v5"/><path d="M8 9a3 3 0 0 0 3-3"/></>,
    hubspot_csv:    <><circle cx="8" cy="8" r="2"/><circle cx="8" cy="3" r="1.3"/><circle cx="12.5" cy="10.5" r="1.3"/><circle cx="3.5" cy="10.5" r="1.3"/><path d="M8 6V4.3M9.4 9.2l2-1.4M6.6 9.2l-2-1.4"/></>,
    bigin_csv:      <><rect x="3" y="3.5" width="10" height="9" rx="1.5"/><path d="M6 3.5v9M3 7h3"/></>,
    zoho_crm_csv:   <><circle cx="8" cy="8" r="5"/><path d="M6 6.5h4l-4 3h4"/></>,
    salesforce_csv: <><path d="M5 9a2.4 2.4 0 1 1 .6-4.7A3 3 0 0 1 11 5a2.3 2.3 0 0 1 .3 4.6q-3.15 0-6.3 0z"/></>,
  };
  return <svg width={size} height={size} viewBox="0 0 16 16" {...p}>{marks[value] || <circle cx="8" cy="8" r="4"/>}</svg>;
}

function avatarColour(name) {
  const palette = ['#5B3FD6','#0B5BB5','#056F4E','#B25400','#066576','#A21C5A','#3F4956','#7A4D00','#2E8FFF','#7C5CFC','#00A878','#C8202E','#8B5CF6','#0EA5E9','#D97706','#DB2777','#059669','#4F46E5'];
  let h = 0; for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) % palette.length;
  return palette[h];
}
function initials(name) {
  return (name || '?').split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}
function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function CrmEmailBatches() {
  const { clientId } = useOutletContext();
  const { getToken } = useAuth();

  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDest, setNewDest] = useState('pipedrive_csv');
  const [showCreate, setShowCreate] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [batchContacts, setBatchContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [exporting, setExporting] = useState(null);

  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const authFetch = useCallback(async (url, opts = {}) => {
    const token = await getToken();
    return fetch(url, { ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
  }, [getToken]);

  const loadBatches = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/clients/${clientId}/email-batches`);
      if (!res.ok) throw new Error('Failed to load batches');
      setBatches(await res.json());
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [authFetch, clientId]);

  const loadBatchContacts = useCallback(async (batchId) => {
    setContactsLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/email-batches/${batchId}/contacts`);
      setBatchContacts(res.ok ? await res.json() : []);
    } catch (e) { setBatchContacts([]); }
    finally { setContactsLoading(false); }
  }, [authFetch]);

  useEffect(() => { loadBatches(); }, [loadBatches]);
  useEffect(() => { if (selectedId) loadBatchContacts(selectedId); }, [selectedId, loadBatchContacts]);

  async function createBatch() {
    if (!newName.trim()) { toast.error('Batch name required'); return; }
    setCreating(true);
    try {
      const res = await authFetch(`${API_BASE}/clients/${clientId}/email-batches`, {
        method: 'POST', body: JSON.stringify({ name: newName.trim(), destination_type: newDest }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error || 'Failed to create batch'); }
      const created = await res.json().catch(() => null);
      setNewName(''); setShowCreate(false);
      toast.success('Batch created');
      await loadBatches();
      if (created && created.id) setSelectedId(created.id);
    } catch (e) { toast.error(e.message); }
    finally { setCreating(false); }
  }

  async function renameBatch(batchId, currentName) {
    const next = window.prompt('Rename batch', currentName);
    if (!next || !next.trim() || next.trim() === currentName) return;
    try {
      const res = await authFetch(`${API_BASE}/email-batches/${batchId}`, { method: 'PUT', body: JSON.stringify({ name: next.trim() }) });
      if (!res.ok) throw new Error('Rename failed');
      toast.success('Renamed'); await loadBatches();
    } catch (e) { toast.error(e.message); }
  }

  async function deleteBatch(batchId, name) {
    if (!window.confirm(`Delete batch "${name}"? This cannot be undone.`)) return;
    try {
      const res = await authFetch(`${API_BASE}/email-batches/${batchId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Batch deleted');
      if (selectedId === batchId) setSelectedId(null);
      await loadBatches();
    } catch (e) { toast.error(e.message); }
  }

  async function exportBatch(batchId) {
    setExporting(batchId);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/email-batches/${batchId}/export`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.message || err.error || 'Export failed'); }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `email-batch-${batchId}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
      await loadBatches();
    } catch (e) { toast.error(e.message); }
    finally { setExporting(null); }
  }

  async function removeContact(batchId, contactId) {
    if (!window.confirm('Remove this contact from the batch?')) return;
    try {
      const res = await authFetch(`${API_BASE}/email-batches/${batchId}/contacts/${contactId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Remove failed');
      toast.success('Contact removed');
      await loadBatchContacts(batchId); await loadBatches();
    } catch (e) { toast.error(e.message); }
  }

  const totalContacts = batches.reduce((s, b) => s + Number(b.contact_count || 0), 0);
  const readyCount = batches.filter(b => b.status !== 'exported' && Number(b.contact_count) > 0).length;
  const exportedCount = batches.filter(b => b.status === 'exported').length;

  const filtered = batches.filter(b => {
    if (statusFilter !== 'all' && (b.status || 'draft') !== statusFilter) return false;
    if (q.trim() && !(b.name || '').toLowerCase().includes(q.trim().toLowerCase())) return false;
    return true;
  });
  const selected = batches.find(b => b.id === selectedId) || null;

  return (
    <div className="crmeb">
      <div className="crmeb-head">
        <div>
          <h1 className="crmeb-title">Email Batches</h1>
          <p className="crmeb-sub">Group contacts with approved email sequences and export a CRM-ready CSV.</p>
        </div>
        <button className="crmeb-new" onClick={() => setShowCreate(v => !v)}>
          <span className="crmeb-new-plus">+</span> New batch
        </button>
      </div>

      <div className="crmeb-stats">
        <div className="crmeb-stat"><div className="crmeb-stat-n">{batches.length}</div><div className="crmeb-stat-l">Batches</div></div>
        <div className="crmeb-stat"><div className="crmeb-stat-n">{totalContacts}</div><div className="crmeb-stat-l">Contacts</div></div>
        <div className="crmeb-stat"><div className="crmeb-stat-n" style={{ color: '#B25400' }}>{readyCount}</div><div className="crmeb-stat-l">Ready to export</div></div>
        <div className="crmeb-stat"><div className="crmeb-stat-n" style={{ color: '#1A7A4C' }}>{exportedCount}</div><div className="crmeb-stat-l">Exported</div></div>
      </div>

      {showCreate && (
        <div className="crmeb-create">
          <input
            className="crmeb-create-name" type="text" autoFocus value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') createBatch(); if (e.key === 'Escape') setShowCreate(false); }}
            placeholder="Batch name (eg. Q4 UK accountants)"
          />
          <div className="crmeb-create-dests">
            {DESTINATIONS.map(d => (
              <button key={d.value} type="button"
                className={'crmeb-destpick' + (newDest === d.value ? ' crmeb-destpick--on' : '')}
                style={newDest === d.value ? { borderColor: d.c, background: d.bg, color: d.c } : undefined}
                onClick={() => setNewDest(d.value)}>
                <DestMark value={d.value} size={14} /> {d.short}
              </button>
            ))}
          </div>
          <button className="crmeb-create-go" onClick={createBatch} disabled={creating}>{creating ? 'Creating…' : 'Create batch'}</button>
        </div>
      )}

      <div className="crmeb-body">
        <div className="crmeb-rail">
          <div className="crmeb-rail-tools">
            <input className="crmeb-search" placeholder="Search batches" value={q} onChange={(e) => setQ(e.target.value)} />
            <div className="crmeb-segbar">
              {['all', 'draft', 'exported'].map(s => (
                <button key={s} className={'crmeb-seg' + (statusFilter === s ? ' crmeb-seg--on' : '')} onClick={() => setStatusFilter(s)}>
                  {s === 'all' ? 'All' : s === 'draft' ? 'Draft' : 'Exported'}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="crmeb-muted">Loading batches…</div>
          ) : filtered.length === 0 ? (
            <div className="crmeb-rail-empty">{batches.length === 0 ? 'No batches yet. Create one to get started.' : 'No batches match.'}</div>
          ) : (
            <div className="crmeb-rail-list">
              {filtered.map(b => {
                const d = dest(b.destination_type);
                const isExported = b.status === 'exported';
                return (
                  <button key={b.id} className={'crmeb-card' + (selectedId === b.id ? ' crmeb-card--on' : '')} onClick={() => setSelectedId(b.id)}>
                    <span className="crmeb-card-dot" style={{ background: isExported ? '#1A7A4C' : '#B25400' }} />
                    <span className="crmeb-card-main">
                      <span className="crmeb-card-name">{b.name}</span>
                      <span className="crmeb-card-meta">
                        <span className="crmeb-card-dest" style={{ color: d.c, background: d.bg }}><DestMark value={b.destination_type} size={12} /> {d.short}</span>
                        <span className="crmeb-card-count">{b.contact_count} {Number(b.contact_count) === 1 ? 'contact' : 'contacts'}</span>
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="crmeb-detail">
          {!selected ? (
            <div className="crmeb-detail-empty">
              <div className="crmeb-detail-empty-mark">✉</div>
              <div className="crmeb-detail-empty-t">Select a batch</div>
              <div className="crmeb-detail-empty-s">Pick a batch on the left to view its contacts and export.</div>
            </div>
          ) : (
            <>
              <div className="crmeb-dhead">
                <div className="crmeb-dhead-l">
                  <div className="crmeb-dhead-top">
                    <h2 className="crmeb-dhead-name">{selected.name}</h2>
                    <span className={'crmeb-status ' + (selected.status === 'exported' ? 'crmeb-status--exp' : 'crmeb-status--draft')}>
                      {selected.status === 'exported' ? 'Exported' : 'Draft'}
                    </span>
                  </div>
                  <div className="crmeb-dhead-meta">
                    <span className="crmeb-card-dest" style={{ color: dest(selected.destination_type).c, background: dest(selected.destination_type).bg }}>
                      <DestMark value={selected.destination_type} size={13} /> {dest(selected.destination_type).label}
                    </span>
                    <span>{selected.contact_count} {Number(selected.contact_count) === 1 ? 'contact' : 'contacts'}</span>
                    <span>Created {formatDate(selected.created_at)}</span>
                  </div>
                </div>
                <div className="crmeb-dhead-actions">
                  <button className="crmeb-btn" onClick={() => renameBatch(selected.id, selected.name)}>Rename</button>
                  <button className="crmeb-btn crmeb-btn--danger" onClick={() => deleteBatch(selected.id, selected.name)}>Delete</button>
                  <button className="crmeb-btn crmeb-btn--primary" disabled={Number(selected.contact_count) === 0 || exporting === selected.id}
                    onClick={() => exportBatch(selected.id)}>
                    {exporting === selected.id ? 'Preparing…' : `Download ${dest(selected.destination_type).short} CSV`}
                  </button>
                </div>
              </div>

              <div className="crmeb-dbody">
                {contactsLoading ? (
                  <div className="crmeb-muted">Loading contacts…</div>
                ) : batchContacts.length === 0 ? (
                  <div className="crmeb-detail-empty">
                    <div className="crmeb-detail-empty-t">No contacts yet</div>
                    <div className="crmeb-detail-empty-s">Add contacts from Sequences using “Add to batch”, then export here.</div>
                  </div>
                ) : (
                  <div className="crmeb-contacts">
                    {batchContacts.map(bc => (
                      <div key={bc.id} className="crmeb-contact">
                        <span className="crmeb-avatar" style={{ background: avatarColour(bc.full_name) }}>{initials(bc.full_name)}</span>
                        <span className="crmeb-contact-main">
                          <span className="crmeb-contact-name">{bc.full_name || '—'}</span>
                          <span className="crmeb-contact-sub">
                            {bc.email ? <span className="crmeb-contact-email">{bc.email}</span> : null}
                            {bc.company_name ? <span className="crmeb-contact-co">{bc.company_name}</span> : null}
                          </span>
                        </span>
                        <button className="crmeb-remove" onClick={() => removeContact(selected.id, bc.contact_id)}>Remove</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
